// @flow

import gql from 'graphql-tag';
import { get, pick } from 'lodash';

import { type ApolloError, useMutation, useQuery } from '@apollo/react-hooks';

export const policiesQuery = gql`
  query pendingPolicies($first: Int, $pending: Boolean) {
    policies(first: $first, pending: $pending) {
      totalCount
      edges {
        title
        contentId
        slug
        tldr
        publishedAt
      }
    }
  }
`;

const policyAgreeMutation = gql`
  mutation PolicyAgreeMutation($slug: String!, $content: Int!) {
    policyAgreement(slug: $slug, content: $content) {
      agreedAt
    }
  }
`;

const updatePoliciesCache = (cache, policySlug, variables) => {
  const { policies } = cache.readQuery({
    query: policiesQuery,
    variables,
  });
  const updated = {
    ...policies,
    edges: policies.edges.filter(p => p.slug !== policySlug),
    totalCount: policies.totalCount - 1,
  };
  cache.writeQuery({
    data: {
      policies: updated,
    },
    query: policiesQuery,
    variables,
  });
};

type Options = {
  first?: number,
  pending?: boolean,
  onMutationError?: (e: ApolloError) => void,
};

const usePolicyData = (options: Options) => {
  const variables = pick(options, ['first', 'pending']);
  const { data, loading } = useQuery(policiesQuery, { variables });
  const policies = get(data, ['policies', 'edges'], []);

  const [agreeToPolicy] = useMutation(policyAgreeMutation, {
    onError: options.onMutationError,
  });

  if (loading) {
    return { policies: [], loading: true };
  }

  const transformedPolicies = policies.map(policy => ({
    ...policy,
    onAccept: () => {
      agreeToPolicy({
        update: cache => {
          updatePoliciesCache(cache, policy.slug, variables);
        },
        variables: { slug: policy.slug, content: policy.contentId },
      });
    },
  }));

  return { policies: transformedPolicies, loading: false };
};

export default usePolicyData;
